import { Component, EventEmitter, Output, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LanguageService } from '../../../services/language.service';
import { Subscription, combineLatest } from 'rxjs';
import { DoseType, OfflineDoseEntry } from '../../../interfaces/product.interface';

export type { OfflineDoseEntry };

const DOSE_OPTIONS: { doseType: DoseType; doseNumber: number }[] = [
  { doseType: 'green', doseNumber: 1 },
  { doseType: 'yellow', doseNumber: 2 },
  { doseType: 'red', doseNumber: 3 }
];

@Component({
  selector: 'app-offline-purchase-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './offline-purchase-modal.html',
  styleUrl: './offline-purchase-modal.scss'
})
export class OfflinePurchaseModalComponent implements OnInit, OnDestroy {
  @Output() confirm = new EventEmitter<OfflineDoseEntry[]>();
  @Output() cancel = new EventEmitter<void>();

  doseOptions = DOSE_OPTIONS;
  selected: { [key: string]: boolean } = {};
  purchaseDates: { [key: string]: string } = {};
  step: 'select' | 'confirm' = 'select';
  today = new Date().toISOString().split('T')[0];
  private subscriptions: Subscription[] = [];

  constructor(
    private languageService: LanguageService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      combineLatest([
        this.languageService.currentLanguage$,
        this.languageService.translations$
      ]).subscribe(() => this.cdr.markForCheck())
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  t(key: string, params?: { [k: string]: string | number }): string {
    return this.languageService.t(key, params);
  }

  canSelect(doseNumber: number): boolean {
    if (doseNumber === 1) return true;
    if (doseNumber === 2) return !!this.selected['green'];
    if (doseNumber === 3) return !!this.selected['green'] && !!this.selected['yellow'];
    return false;
  }

  isDisabled(doseNumber: number): boolean {
    return !this.canSelect(doseNumber);
  }

  toggleDose(doseType: DoseType, doseNumber: number): void {
    if (this.isDisabled(doseNumber)) return;
    this.selected[doseType] = !this.selected[doseType];
    if (!this.selected[doseType]) {
      delete this.purchaseDates[doseType];
      if (doseType === 'green') {
        this.selected['yellow'] = false;
        this.selected['red'] = false;
        delete this.purchaseDates['yellow'];
        delete this.purchaseDates['red'];
      } else if (doseType === 'yellow') {
        this.selected['red'] = false;
        delete this.purchaseDates['red'];
      }
    }
    this.cdr.markForCheck();
  }

  getSelectedWithDates(): OfflineDoseEntry[] {
    return DOSE_OPTIONS
      .filter(d => this.selected[d.doseType] && this.purchaseDates[d.doseType])
      .map(d => ({
        doseType: d.doseType,
        doseNumber: d.doseNumber,
        purchaseDate: this.purchaseDates[d.doseType]
      }))
      .sort((a, b) => a.doseNumber - b.doseNumber);
  }

  allDatesFilled(): boolean {
    const sel = this.getSelectedWithDates();
    const count = Object.keys(this.selected).filter(k => this.selected[k]).length;
    return sel.length === count && sel.length > 0;
  }

  /** Purchase dates must be in sequence: Dose 1 <= Dose 2 <= Dose 3 (per product logic). */
  datesInSequence(): boolean {
    const entries = this.getSelectedWithDates();
    if (entries.length <= 1) return true;
    for (let i = 1; i < entries.length; i++) {
      const prev = new Date(entries[i - 1].purchaseDate).getTime();
      const curr = new Date(entries[i].purchaseDate).getTime();
      if (curr < prev) return false;
    }
    return true;
  }

  /** Error message when dates are out of sequence. */
  sequenceError(): string | null {
    if (this.datesInSequence() || !this.allDatesFilled()) return null;
    return this.t('offlinePurchaseSequenceError') || null;
  }

  canProceedToConfirm(): boolean {
    return this.allDatesFilled() && this.datesInSequence();
  }

  goToConfirm(): void {
    if (!this.canProceedToConfirm()) return;
    this.step = 'confirm';
    this.cdr.markForCheck();
  }

  backToSelect(): void {
    this.step = 'select';
    this.cdr.markForCheck();
  }

  onConfirm(): void {
    const entries = this.getSelectedWithDates();
    if (entries.length && this.datesInSequence()) this.confirm.emit(entries);
  }

  onCancel(): void {
    this.cancel.emit();
  }

  formatDateForSummary(iso: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  }

  onDateChange(): void {
    this.cdr.markForCheck();
  }

  getDoseLabel(doseType: DoseType): string {
    const labels: Record<string, string> = {
      green: this.t('doseGreen') || 'Green (Dose 1)',
      yellow: this.t('doseYellow') || 'Yellow (Dose 2)',
      red: this.t('doseRed') || 'Red (Dose 3)'
    };
    return labels[doseType] || doseType;
  }
}
